import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, favoritesAPI, ordersAPI } from '../api/axios';
import ProductCard from '../components/ProductCard';
import EditProfileModal from '../components/EditProfileModal';
import { getPaymentStatusMeta } from '../utils/paymentStatus';
import { 
  FaUser, 
  FaHeart, 
  FaShoppingBag, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEdit,
  FaHistory,
  FaSignOutAlt,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaTruck,
  FaExclamationTriangle,
  FaSpinner,
  FaEnvelope
} from 'react-icons/fa';

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('favorites');
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [profileResponse, favoritesResponse] = await Promise.all([
        authAPI.getProfile(),
        favoritesAPI.getFavorites()
      ]);
      
      setUser(profileResponse.data);
      setFavorites(favoritesResponse.data);
      
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Error al cargar los datos del perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const response = await ordersAPI.getAllOrders();
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleUpdateProfile = async (formData) => {
    try {
      const response = await authAPI.updateProfile(formData);
      setUser(response.data.user);
      setSuccessMessage('Perfil actualizado correctamente');
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error(error.response?.data?.message || 'Error al actualizar el perfil');
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    window.location.href = '/';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return <FaCheckCircle className="text-green-500 mr-2" />;
      case 'processing':
        return <FaClock className="text-yellow-500 mr-2" />;
      case 'shipped':
        return <FaTruck className="text-blue-500 mr-2" />;
      case 'pending':
        return <FaClock className="text-gray-500 mr-2" />;
      case 'cancelled':
        return <FaExclamationTriangle className="text-red-500 mr-2" />;
      default:
        return <FaBox className="text-gray-500 mr-2" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'delivered':
        return 'Entregado';
      case 'completed':
        return 'Completado';
      case 'processing':
        return 'En proceso';
      case 'shipped':
        return 'Enviado';
      case 'pending':
        return 'Pendiente';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || 'U';

  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0) {
      fetchOrders();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0C4B45]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchUserData}
            className="bg-[#0C4B45] text-white px-6 py-2 rounded-lg hover:bg-[#083D38] transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4fbfa] via-white to-[#f8f5fa] py-8 pt-24">
      {successMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-3 rounded-lg shadow-lg">
            {successMessage}
          </div>
        </div>
      )}

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user}
        onUpdate={handleUpdateProfile}
      />

      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="relative mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-[#0C4B45] via-[#12665d] to-[#083D38] p-6 shadow-xl md:p-8">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#83F4E9]/10 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-[#F2A9FD]/10 blur-2xl" />
            <div className="relative flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-2xl font-bold text-white shadow-lg backdrop-blur">
                  {userInitial}
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#83F4E9]">Mi cuenta</p>
                  <h1 className="text-2xl font-bold text-white md:text-3xl">Hola, {user?.name || 'Usuario'}</h1>
                  <p className="mt-1 text-sm text-white/75">{user?.email}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setIsEditModalOpen(true)} className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#0C4B45] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#83F4E9]">
                  <FaEdit /> Editar perfil
                </button>
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20"
                >
                  <FaSignOutAlt /> Cerrar sesión
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <div className="mb-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center justify-between"><h2 className="text-lg font-bold text-[#0C4B45]">Información personal</h2><span className="rounded-full bg-[#83F4E9]/20 px-2.5 py-1 text-xs font-semibold capitalize text-[#0C4B45]">{user?.role || 'Cliente'}</span></div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                    <FaUser className="text-[#662D8F]" />
                    <div>
                      <p className="text-xs font-medium text-slate-500">Nombre completo</p>
                      <p className="font-semibold text-slate-800">{user?.name || 'Sin registrar'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                    <FaEnvelope className="text-[#662D8F]" />
                    <div>
                      <p className="text-xs font-medium text-slate-500">Correo electrónico</p>
                      <p className="break-all font-semibold text-slate-800">{user?.email || 'Sin registrar'}</p>
                    </div>
                  </div>
                  
                  {user?.phone && (
                    <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                      <FaPhone className="text-[#662D8F]" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Teléfono</p>
                        <p className="font-semibold text-slate-800">{user.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {user?.address && (
                    <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
                      <FaMapMarkerAlt className="text-[#662D8F] mt-1" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Dirección</p>
                        <p className="font-semibold text-slate-800">
                          {user.address.street}<br />
                          {user.address.city}, {user.address.state} {user.address.zipCode}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                <button onClick={() => setIsEditModalOpen(true)} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-[#0C4B45]/15 py-2.5 text-sm font-semibold text-[#0C4B45] transition hover:bg-[#0C4B45] hover:text-white"><FaEdit /> Actualizar mis datos</button>
              </div>
              
              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-lg font-bold text-[#0C4B45]">Mi actividad</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl bg-[#83F4E9]/15 p-4 text-center">
                    <FaHeart className="text-[#662D8F] text-2xl mx-auto mb-2" />
                    <p className="font-bold text-xl">{favorites.length}</p>
                    <p className="text-sm text-gray-600">Favoritos</p>
                  </div>
                  
                  <div className="rounded-xl bg-[#F2A9FD]/15 p-4 text-center">
                    <FaShoppingBag className="text-[#662D8F] text-2xl mx-auto mb-2" />
                    <p className="font-bold text-xl">{orders.length}</p>
                    <p className="text-sm text-gray-600">Pedidos</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="mb-5 rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('favorites')}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                      activeTab === 'favorites'
                        ? 'bg-[#0C4B45] text-white shadow-sm'
                        : 'text-gray-500 hover:bg-[#0C4B45]/5 hover:text-[#0C4B45]'
                    }`}
                  >
                    <FaHeart className="inline mr-2" /> Mis Favoritos
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                      activeTab === 'orders'
                        ? 'bg-[#0C4B45] text-white shadow-sm'
                        : 'text-gray-500 hover:bg-[#0C4B45]/5 hover:text-[#0C4B45]'
                    }`}
                  >
                    <FaHistory className="inline mr-2" /> Mis Pedidos
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:p-6">
                {activeTab === 'favorites' ? (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-[#0C4B45]">Mis Productos Favoritos</h2>
                      <span className="bg-[#F2A9FD] text-[#662D8F] px-3 py-1 rounded-full text-sm font-medium">
                        {favorites.length} productos
                      </span>
                    </div>

                    {favorites.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {favorites.map(product => (
                          <ProductCard key={product._id} product={product} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🤍</div>
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">No tienes favoritos aún</h3>
                        <p className="text-gray-500 mb-6">Explora nuestros productos y guarda tus favoritos haciendo clic en el corazón</p>
                        <Link 
                          to="/products"
                          className="bg-[#662D8F] text-white px-6 py-2 rounded-lg hover:bg-[#4F1F73] transition-colors"
                        >
                          Explorar Productos
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-[#0C4B45]">Historial de Pedidos</h2>
                      <button
                        onClick={fetchOrders}
                        disabled={ordersLoading}
                        className="flex items-center gap-2 px-3 py-1 bg-[#83F4E9] text-[#0C4B45] rounded-lg hover:bg-[#6ad4c9] transition-colors disabled:opacity-50"
                      >
                        {ordersLoading ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaHistory />
                        )}
                        Actualizar
                      </button>
                    </div>
                    
                    {ordersLoading ? (
                      <div className="flex justify-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-[#0C4B45]" />
                      </div>
                    ) : orders.length > 0 ? (
                      <div className="space-y-4">
                        {orders.map(order => {
                          const payment = getPaymentStatusMeta(order.paymentStatus);
                          return (
                          <div key={order._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-semibold">Pedido #{order.orderNumber}</p>
                                <p className="text-sm text-gray-500">
                                  {new Date(order.createdAt).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1.5">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                  {getStatusIcon(order.status)}
                                  {getStatusText(order.status)}
                                </span>
                                <span className={`border px-2.5 py-1 rounded-full text-xs font-semibold ${payment.className}`}>
                                  {payment.label}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <p className="text-gray-600">
                                {order.items?.length || 0} producto(s)
                              </p>
                              <p className="font-bold text-lg">
                                ${order.total?.toFixed(2) || '0.00'}
                              </p>
                            </div>
                            
                            
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📦</div>
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">No tienes pedidos aún</h3>
                        <p className="text-gray-500 mb-6">Realiza tu primera compra y encuentra aquí el historial de tus pedidos</p>
                        <Link 
                          to="/products"
                          className="bg-[#662D8F] text-white px-6 py-2 rounded-lg hover:bg-[#4F1F73] transition-colors"
                        >
                          Comprar Ahora
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
